// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// modes.ts — the clock, every production constant, and the three modes.
//
// A MODE HAS TO CHANGE HOW THE GAME PLAYS, not just a number. The test each of these had to pass is:
// does a strategy that wins here LOSE in the other two? tests/balance.test.ts asserts exactly that —
// argmax(policy) must differ across at least two modes — so a mode that collapsed into another would
// turn the suite red rather than quietly shipping as a difficulty dial.
//
//   • longwinter — GROWTH. Twelve days, six souls, forage and stands regrow, and strangers turn up on
//     days 3, 6 and 9 who you may refuse. The question is "is this mouth worth the hands?".
//   • fastness   — DRAWDOWN. Ten days, nine souls, a fixed roster, 60% resource density and NOTHING
//     regrows: a stand you cut is gone for the winter. Illness is likelier, so who sleeps where
//     starts to matter. The question is "there is no more; can you out-ROUTE a shortfall you cannot
//     out-produce?".
//   • crossing   — RELOCATION. Eight days, five souls, a ruined camp in the south and a sound
//     longhouse in the north joined by a four-tile neck. At dusk on day 4 everything south of the
//     neck gains two permanent snow depth and the southern firepit cannot be relit. The question is
//     "can you physically move a colony through a four-tile neck before the storm plugs it?".
//
// EVERY NUMBER BELOW IS INTEGER. Needs are centi-units, snow is an int plus an int accumulator,
// durations are ticks. The simulation must reach the same answer on every device — the shared daily
// seed and the balance sim both depend on it — and tests/source-hygiene.test.ts fails the build on a
// decimal literal, a transcendental or a clock read anywhere in the simulation's files.

import type { MapId } from './map';

// ── the clock ───────────────────────────────────────────────────────────────

/** One tick is 100ms of colony time. Driven by setInterval, NEVER by rAF — see clock.ts. */
export const TICK_MS = 100;
/** 900 ticks = 90 seconds at 1x. */
export const TICKS_PER_DAY = 900;
/** dawn / day / dusk / night. The weather schedule is indexed by quarter. */
export const TICKS_PER_QUARTER = 225;
export const QUARTERS_PER_DAY = 4;
export const QUARTER_NAMES = ['dawn', 'day', 'dusk', 'night'] as const;
/** Snow accumulates in batches rather than every tick: 143 tiles x 10 Hz is pointless work. */
export const SNOW_BATCH = 50;
/** Illness rolls once per quarter. The game has quarters, not hours. */
export const ILLNESS_PERIOD = TICKS_PER_QUARTER;

// ── needs ───────────────────────────────────────────────────────────────────

/** Needs run 0..10000 in centi-units. */
export const NEED_MAX = 10000;
export const HUNGER_PER_TICK = 4;
/**
 * A meal buys 625 ticks, so six souls need 8.6 meals a day.
 *
 * MEASURED, NOT CHOSEN. At 1000 the arithmetic said six souls need 21.6 meals a day and a level-3
 * cook could theoretically make 30, which looked like a tight, interesting margin. The sim said
 * otherwise: a real cook spends most of its day WALKING — to a store for food, to the stove, back —
 * and the whole colony managed 45 meals in twelve days against a demand of 259. Theoretical
 * throughput is not throughput. 2500 with a four-meal recipe puts real production and real demand in
 * the same neighbourhood, which is where a difficulty curve can exist at all.
 */
export const MEAL_VALUE = 2500;
/** Seek food here, well before health starts draining at NEED_MAX. */
export const EAT_AT = 6000;
/**
 * Cold, and this constant killed every colony in the sim's first run. At 6 a working colonist reaches
 * NEED_MAX in 1583 ticks — under two days — and then loses 14 health a tick, so the entire fleet
 * froze on day 2 with a full larder. Halved, and paired with WARM_AT below, so that going to the fire
 * is a thing colonists DO rather than something the player has to micromanage.
 */
export const COLD_PER_TICK = 3;
export const COLD_RECOVERY = 18;
/**
 * A colonist this cold stops working and goes to the nearest heated room, staying until COLD_OK.
 *
 * This is not a comfort feature, it is the mechanism that makes interaction C exist: when the door
 * has drifted shut and the room is no longer heated, the colonist walks in, gets no warmer, and the
 * illness roll — which is weighted by exactly this number — starts finding them. Without the
 * behaviour the player would have to hand-drive every colonist to the fire, and the failure would
 * look like the player's inattention rather than the colony's broken heat.
 */
export const WARM_AT = 7000;
export const COLD_OK = 2500;
export const TIRED_PER_TICK = 3;
export const TIRED_RECOVERY = 25;
/** Above this, a colonist works at half speed. This is what makes a bed worth building. */
export const TIRED_SLOW_AT = 7000;
export const SLEEP_AT = 7500;
export const HEALTH_DRAIN_HUNGER = 8;
export const HEALTH_DRAIN_COLD = 14;
/** Health regained per tick when warm, fed, rested and not ill. */
export const HEALTH_RECOVERY = 3;

// ── weather ─────────────────────────────────────────────────────────────────

export const enum Phase {
  Calm = 0,
  Snow = 1,
  Storm = 2,
}
export const PHASE_NAMES = ['calm', 'snow', 'storm'] as const;

/**
 * Accumulation per 50-tick batch, before the shelter multiplier.
 *
 * CALM IS NOT ZERO ON PURPOSE. A zero switches the antagonist off for whole stretches and leaves the
 * player with nothing to do but wait — which is the classic way a colony sim's first minute is
 * boring. At 4 a sheltered corner silts up over about a day, so there is always a little honest work
 * and the drift geometry keeps evolving.
 */
export const PHASE_RATE: Record<Phase, number> = {
  [Phase.Calm]: 4,
  [Phase.Snow]: 9,
  [Phase.Storm]: 26,
};

/**
 * SNOW DRIFTS AGAINST OBSTRUCTIONS, and this is the single most important constant group in the
 * game. The multiplier is `(1 + min(shelter, SHELTER_CAP)) / SHELTER_DIV`, where `shelter` counts a
 * tile's four neighbours that are impassable — rock, wall, closed door, a building, or snow already
 * at depth 3.
 *
 * The first draft of this rule had drifts growing from DRIFTS (`1 + neighboursAtDepth>=2`). Three
 * independent critics simulated it and all three found the same thing: a 3-wide corridor severed in
 * 90s and a 1-wide in 95s — a ratio of 0.95x against a design that claimed 4.5x. Widening the artery,
 * the game's central skill expression, was pure cost with a slightly NEGATIVE benefit. The cause is
 * structural, not a tuning miss: before anything reaches depth 2 the term is zero for every tile
 * whatever the width, and once it engages the wider corridor has MORE mutually-reinforcing
 * neighbours. No amount of tuning fixes a sign.
 *
 * Measured with the drift-against-obstructions rule, ticks until EVERY tile of a gap of width W in
 * the ridge reaches depth 3 under a continuous phase:
 *
 *            width 1   width 2   width 3   width 1 + path
 *   storm      400       600       800        800
 *   snow      1200        --      2350       2500
 *   calm      2500        --      5000       5000
 *
 * A storm always runs at least two consecutive quarters = 450 ticks, and 400 < 450 < 800 is the
 * inequality the whole design rests on: a one-tile corridor plugs inside a single storm and a
 * three-tile corridor survives it. tests/rules.test.ts asserts the PROPERTY (strictly increasing in
 * W, and 1 severs inside a storm while 3 does not), never the seconds.
 */
export const SHELTER_CAP = 3;
export const SHELTER_DIV = 2;
/** Overflow threshold for the fill accumulator. */
export const SNOW_OVERFLOW = 100;
export const MAX_DEPTH = 3;
/** Clearing one tile takes this many ticks of work. */
export const clearTicks = (depth: number): number => 12 + 8 * depth;
/** A storm is never shorter than this, or the inequality above stops meaning anything. */
export const MIN_STORM_QUARTERS = 2;

// ── production ──────────────────────────────────────────────────────────────

export const CARRY_CAP = 4;
/**
 * A stockpile holds this much. It was 40, which the balance sim showed was a live pathology rather
 * than a tuning miss: the single starting store filled inside two days, every subsequent delivery
 * spilled onto the ground, each spill created a HAUL job, and the hauler picked the pile straight
 * back up and spilled it again — 800 spills in a twelve-day run. The cap is now generous, AND a HAUL
 * job is only created when some store with space is reachable, which is what actually closes the loop.
 */
export const STOCKPILE_CAP = 120;
/** At most six, which is what keeps the Dijkstra destination set provably <= 8. */
export const MAX_STOCKPILES = 6;

export const STAND_YIELD = 6;
export const FORAGE_YIELD = 4;
export const CUT_TICKS = 120;
export const FORAGE_TICKS = 90;
export const STAND_REGROW = 1800;
export const FORAGE_REGROW = 1350;

/**
 * 2 food + 1 wood -> 4 meals. The recipe is deliberately CHUNKY: every craft costs a round trip to a
 * store and back, so a recipe that yields two meals spends more of the cook's day walking than
 * cooking, and the colony starves with food in the larder.
 */
export const MEAL_IN_FOOD = 2;
export const MEAL_IN_WOOD = 1;
export const MEAL_OUT = 4;
export const MEAL_TICKS = 180;
/** 2 food + 1 wood -> 1 remedy, at the SAME building, contending for the same worker. */
export const REMEDY_IN_FOOD = 2;
export const REMEDY_IN_WOOD = 1;
export const REMEDY_TICKS = 240;

/**
 * Fuel is reciprocal in the area heated: 200 ticks per wood at the 12-tile reference room, 80 at 30
 * tiles, 24 at 99, 17 at the whole board. Doubled during a storm.
 *
 * The earlier form had a FLAT ZONE, which meant heating a huge enclosure cost the same as heating a
 * small one — and since enclosed tiles never accumulate snow, walling the map both deleted the snow
 * system and heated everything for free. The `max(20, ...)` is only a divide-by-small guard and sits
 * below any reachable room size. A `waller` policy in the balance sim asserts this is enough.
 */
/**
 * How many burn-periods of warmth one log actually gives.
 *
 * Warmth used to be free — a room with a firepit in it was warm whether or not any wood had ever
 * reached the fire, which meant the wood supply chain, half the game, had no effect on the thing that
 * kills people. Making warmth expire fixed that and immediately killed every colony in the sim,
 * because at one period per log a moment's unreachable woodpile put the fire out at once. A log now
 * carries the room for a while: the pressure the design wants is that the wood cannot be REACHED,
 * not that it is consumed a second faster than it can be carried.
 */
export const LOG_BURN = 6;

export const burnPeriod = (roomTiles: number, storm: boolean): number => {
  const base = Math.max(20, Math.round(2400 / Math.max(1, roomTiles)));
  return storm ? Math.max(10, base >> 1) : base;
};

export const enum Build {
  Path = 0,
  Wall = 1,
  Door = 2,
  Bed = 3,
  Stockpile = 4,
  Firepit = 5,
  Stove = 6,
}
export const BUILD_IDS = [
  Build.Path,
  Build.Wall,
  Build.Door,
  Build.Bed,
  Build.Stockpile,
  Build.Firepit,
  Build.Stove,
] as const;

export interface BuildSpec {
  id: Build;
  key: string;
  name: string;
  wood: number;
  ticks: number;
  /** Blocks movement and line of enclosure. */
  solid: boolean;
}

export const BUILDS: Record<Build, BuildSpec> = {
  [Build.Path]: { id: Build.Path, key: 'path', name: 'Path', wood: 1, ticks: 30, solid: false },
  [Build.Wall]: { id: Build.Wall, key: 'wall', name: 'Wall', wood: 2, ticks: 60, solid: true },
  [Build.Door]: { id: Build.Door, key: 'door', name: 'Door', wood: 3, ticks: 90, solid: false },
  [Build.Bed]: { id: Build.Bed, key: 'bed', name: 'Bed', wood: 3, ticks: 90, solid: false },
  [Build.Stockpile]: { id: Build.Stockpile, key: 'store', name: 'Stockpile', wood: 4, ticks: 120, solid: false },
  [Build.Firepit]: { id: Build.Firepit, key: 'pit', name: 'Firepit', wood: 5, ticks: 150, solid: false },
  [Build.Stove]: { id: Build.Stove, key: 'stove', name: 'Stove', wood: 8, ticks: 240, solid: false },
};

// ── the modes ───────────────────────────────────────────────────────────────

export interface Mode {
  id: string;
  name: string;
  map: MapId;
  days: number;
  /** Souls at dawn on day 1. */
  souls: number;
  /** Do stands and forage come back? */
  regrow: boolean;
  /** Percent of the map's stands and forage patches that actually hold anything. */
  density: number;
  /** Days a stranger appears on. Empty means a fixed roster. */
  arrivals: readonly number[];
  /** Per-quarter illness weight, in hundredths of a percent per centi-unit of (cold + tired). */
  illnessWeight: number;
  /** A flat per-quarter illness chance in hundredths of a percent, independent of cold. */
  illnessBase: number;
  /** Starting stores, in units. */
  startWood: number;
  startFood: number;
  startMeals: number;
  /** crossing only: the permanent drift falls at dusk on this day. */
  abandonDay: number;
  blurb: string;
  /** One line for the how-to-play card, which has to state what differs per mode. */
  rule: string;
}

export const MODES: Record<string, Mode> = {
  longwinter: {
    id: 'longwinter',
    name: 'Longwinter',
    map: 'home',
    days: 12,
    souls: 6,
    regrow: true,
    density: 100,
    arrivals: [3, 6, 9],
    illnessWeight: 3,
    illnessBase: 60,
    startWood: 14,
    startFood: 8,
    startMeals: 6,
    abandonDay: 0,
    blurb:
      'Twelve days, six souls, and a wood that grows back. Strangers come out of the snow on the third, sixth and ninth day, and every one of them is a pair of hands and another mouth — you may turn them away, and sometimes you should. The colony can grow here, which is exactly what makes it possible to overreach.',
    rule: 'Twelve days. Forage regrows. Strangers arrive — take them or turn them away.',
  },
  fastness: {
    id: 'fastness',
    name: 'Fastness',
    map: 'home',
    days: 10,
    souls: 9,
    regrow: false,
    density: 60,
    arrivals: [],
    illnessWeight: 5,
    illnessBase: 130,
    startWood: 30,
    startFood: 22,
    startMeals: 14,
    abandonDay: 0,
    blurb:
      'Nine of you, ten days, and nothing comes back. Every stand you cut is gone for the winter and there are four in ten fewer of them to begin with. Nobody arrives to help. You cannot out-produce this shortfall, so the only thing left is to out-route it — and with nine sleeping close, the sickness spreads.',
    rule: 'Nothing regrows and nobody arrives. Nine mouths, ten days, and a fixed store.',
  },
  crossing: {
    id: 'crossing',
    name: 'The Crossing',
    map: 'crossing',
    days: 8,
    souls: 5,
    regrow: true,
    density: 100,
    arrivals: [],
    illnessWeight: 3,
    illnessBase: 60,
    startWood: 20,
    startFood: 14,
    startMeals: 9,
    abandonDay: 4,
    blurb:
      'Your camp is in the south and it is finished. A sound longhouse stands in the north, and between them is a neck four tiles wide. At dusk on the fourth day the south goes under for good and the fire there cannot be lit again. Everything you want on the fifth day has to come through that neck, four units to an armload, while the storm tries to close it.',
    rule: 'Move everything north through the neck before dusk on day 4. The south is lost.',
  },
};

export const MODE_IDS: string[] = ['longwinter', 'fastness', 'crossing'];

/** Longwinter is the default because it is the only mode where the colony can GROW, which is what
 *  teaches the trade the other two are built out of. */
export const DEFAULT_MODE = 'longwinter';

/**
 * Guard an id off a URL or localStorage. `MODES[id] ||` lets 'constructor' through as a Mode of
 * undefined fields, which reaches the generator as NaN and produces a board of holes.
 */
export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}

/** Meals the colony consumes per day. Exact, analytic, defined at t=0 — never sampled. */
export function mealsPerDay(souls: number): number {
  return (souls * TICKS_PER_DAY * HUNGER_PER_TICK) / MEAL_VALUE;
}
